import { FunctionComponent, useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { format } from "date-fns";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { useMarkScrapeRequestDeliveredMutation } from "@/pages/Private/redux/customEvent/customEvent.api";

import { useSnackBar } from "@/utils/useSnackbar";

import { CustomEvent } from "../schema/customEvent";
import { DeliverScrapeFileModal } from "./DeliverScrapeFileModal";
import { ScrapeRequestStatus } from "../schema/scrapeRequest";
import { deliveryErrorKey, unclaimedFileId } from "./scrapeDelivery.errors";

export interface ScrapeRequestQueueCellProps {
	customEvent: CustomEvent;
}

/**
 * What this source still owes (PREC-3091): the open scrape request, who ordered it and when, plus
 * the way to close it when the file was attached out of band. Sources with nothing pending stay
 * visually quiet so the queue is what stands out.
 *
 * Every action here reports its outcome (PREC-3974). Before that the mutation was fired without
 * being awaited, so a refusal — the normal answer on a source with no file yet — was invisible and
 * read as a button with no function.
 */
export const ScrapeRequestQueueCell: FunctionComponent<ScrapeRequestQueueCellProps> = ({
	customEvent,
}) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`customEvent.rescrape.admin.${key}`), [t]);

	const [markDelivered, { isLoading }] = useMarkScrapeRequestDeliveredMutation();

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const [showDeliverModal, setShowDeliverModal] = useState<boolean>(false);

	const openRequest = customEvent.scrapeRequests?.find(
		(request) => request.status === ScrapeRequestStatus.REQUESTED
	);

	// The API closes the request against a file no earlier delivery claims, so with nothing
	// unclaimed the escape hatch can only be refused — say so instead of offering the click.
	const claimableFile = unclaimedFileId(customEvent);

	const handleMarkDelivered = useCallback(async () => {
		if (!openRequest) {
			return;
		}

		try {
			const delivered = await markDelivered({ requestId: openRequest.id }).unwrap();

			// PREC-4456: a cancel got there first, so the file became a new delivery.
			handleOpenSnackbar(
				ts(delivered?.cancelledMeanwhileRequestId ? "deliveredAfterCancel" : "markedDelivered"),
				delivered?.cancelledMeanwhileRequestId ? 8000 : 4000
			);
		} catch (err) {
			console.error(err);
			handleOpenSnackbar(ts(`errors.${deliveryErrorKey(err)}`), 6000);
		}
	}, [openRequest, markDelivered, handleOpenSnackbar, ts]);

	if (!openRequest) {
		return <div className="text-sm text-text-meta">{ts("nonePending")}</div>;
	}

	return (
		<div className="flex flex-col items-start gap-1">
			<span className="rounded-xl bg-status-running-light px-2 py-0.5 text-xs font-medium text-status-running">
				{openRequest.isInitial ? ts("initialPending") : ts("rescrapePending")}
			</span>
			<span className="text-xs text-text-meta">
				{openRequest.requestedAt && format(new Date(openRequest.requestedAt), "dd.MM.yyyy")}
				{openRequest.requestedByName ? ` · ${openRequest.requestedByName}` : ""}
			</span>
			<div className="flex flex-row flex-wrap gap-2">
				{/* The delivery itself: upload the file and the request closes with it. */}
				<Button
					color={ButtonColor.ACTION}
					size={ButtonSize.XS}
					title={ts("deliver.action")}
					onClick={() => setShowDeliverModal(true)}
				/>
				{/* Escape hatch for a file attached outside this flow. The title sits on the wrapper
				    because a disabled button receives no pointer events of its own. */}
				<span title={claimableFile ? undefined : ts("errors.noUnclaimedFile")}>
					<Button
						className="disabled:cursor-not-allowed"
						color={ButtonColor.ACTION_SECONDARY}
						disabled={!claimableFile}
						// Without the grey treatment the disabled button is indistinguishable from the
						// live one — which would just recreate "the button does nothing" (PREC-3974).
						disabledInGray
						isLoading={isLoading}
						size={ButtonSize.XS}
						title={ts("markDelivered")}
						onClick={handleMarkDelivered}
					/>
				</span>
			</div>

			{showDeliverModal && (
				<DeliverScrapeFileModal
					customEvent={customEvent}
					handleClose={() => setShowDeliverModal(false)}
				/>
			)}

			{renderMessageSnackbar()}
		</div>
	);
};
